import React from 'react';
import _ from 'lodash';

import rxUi from 'ub/control/rx-ui';
import SOURCES from 'ui/asset-library/asset-sources';
import { TYPES } from 'ui/asset-library/asset-types';
import { assetTypePropType } from './prop-types';

import AssetLibrary from './asset-library';

import { trackUserEvent } from 'ub/ui-user-tracking';

const nullSource = {
  isFetching: false,
  items: [],
  lastPageFetched: 0,
  totalPages: null,
};

function generateTypeTotals(source) {
  if (source.lastPageFetched === 0) {
    return {};
  }

  return Object.keys(TYPES).reduce(
    (acc, type) => ({ ...acc, [type]: source.items.filter(item => item.type === type).length }),
    {}
  );
}

function generateSourceTotals(sources) {
  return Object.keys(SOURCES).reduce(
    (acc, source) =>
      sources[source].lastPageFetched === 0
        ? acc
        : {
            ...acc,
            [source]: sources[source].items.filter(item => item.type === 'images').length,
          },
    {}
  );
}

function compareItemsForSort(a, b, sortBy, sortAscending) {
  const first = sortAscending ? a : b;
  const second = sortAscending ? b : a;

  switch (sortBy) {
    case 'filename':
      return first.asset.content_file_name === second.asset.content_file_name
        ? compareItemsForSort(a, b, 'date', sortAscending)
        : first.asset.content_file_name.localeCompare(second.asset.content_file_name);

    case 'filesize':
      return first.asset.content_file_size === second.asset.content_file_size
        ? compareItemsForSort(a, b, 'date', sortAscending)
        : first.asset.content_file_size > second.asset.content_file_size
          ? 1
          : -1;

    case 'date':
    default:
      return first.asset.id > second.asset.id ? 1 : -1;
  }
}

export default class AssetLibraryContainer extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      authToken: null,
      failedUploads: [],
      isConfirmingDelete: false,
      isOpen: false,
      searchTerm: '',
      selectedItemIds: [],
      selectedSource: 'currentClient',
      selectedType: 'images',
      sortAscending: false,
      sortBy: 'date',
      sources: Object.keys(SOURCES).reduce((acc, source) => ({ ...acc, [source]: nullSource }), {}),
      uploadsInProgress: 0,
    };

    this.uiBinding = new rxUi.Binding(['AssetLibrary']);
    this.uiBinding.subscribe(this.handleUIEvent.bind(this));

    this.dispatch = this.dispatch.bind(this);
    this.uploadFiles = this.uploadFiles.bind(this);
    this.handleScrollNearBottom = this.handleScrollNearBottom.bind(this);

    this.handleSearchTermChange = _.debounce(this.handleSearchTermChange.bind(this), 500);
  }

  componentDidMount() {
    const authTokenMetaTag = document.querySelector('meta[name=csrf-token]');

    if (authTokenMetaTag) {
      this.setState({ authToken: authTokenMetaTag.content });
    }
  }

  componentWillReceiveProps(nextProps) {
    this.setState({
      isOpen: nextProps.isOpen,
      selectedItemIds: nextProps.selectedAssetUuids || [],
      selectedType: nextProps.selectedType || this.state.selectedType,

      // AssetLibraryWrapper only re-renders AssetLibraryContainer with new props when triggering an
      // open, so we can also use componentWillReceiveProps to reset some state each time the asset
      // library is opened.
      failedUploads: [],
      isConfirmingDelete: false,
      searchTerm: '',
      selectedSource: 'currentClient',
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.isOpen && !prevState.isOpen) {
      this.fetchItems(1, false);
    }
  }

  fetchItems(page, replaceExisting) {
    const { searchTerm, selectedSource, sources } = this.state;
    const sourceConfig = SOURCES[selectedSource];

    if (page <= sources[selectedSource].lastPageFetched && !replaceExisting) {
      return;
    }

    this.setState({
      sources: { ...sources, [selectedSource]: { ...sources[selectedSource], isFetching: true } },
    });

    const fetch =
      sourceConfig.fetchByKeyword && searchTerm
        ? sourceConfig.fetchByKeyword(searchTerm, page)
        : sourceConfig.fetch(page);

    fetch
      .then(({ fetchedItems, totalPages }) =>
        this.setState({
          sources: {
            ...sources,
            [selectedSource]: {
              isFetching: false,
              items: replaceExisting
                ? fetchedItems
                : [...sources[selectedSource].items, ...fetchedItems],
              lastPageFetched: page,
              totalPages,
            },
          },
        })
      )
      .catch(fetchError =>
        this.setState({
          sources: {
            ...sources,
            [selectedSource]:
              page === 1
                ? { ...nullSource, fetchError }
                : { ...sources[selectedSource], isFetching: false },
          },
        })
      );
  }

  dispatch(event) {
    this.uiBinding.onNext(event);
  }

  handleUIEvent(event) {
    switch (event.type) {
      case 'changeType':
        return this.setState({
          isConfirmingDelete: false,
          selectedType: event.assetType,
        });

      case 'toggleSortDirection':
        return this.setState({
          isConfirmingDelete: false,
          sortAscending: !this.state.sortAscending,
        });

      case 'changeSort':
        // 'Created' should default to descending sort order, all others should default to
        // ascending.
        return this.setState({
          isConfirmingDelete: false,
          sortAscending: event.sortBy === 'date' ? false : true,
          sortBy: event.sortBy,
        });

      case 'changeSource':
        return this.handleSourceChange(event.source);

      case 'selectItem':
        return this.setState({ selectedItemIds: this.calculateNewSelectedItemIds(event) });

      case 'choose': {
        const chosenItem = this.state.sources[this.state.selectedSource].items.find(
          item => item.id === this.state.selectedItemIds[0]
        );

        if (SOURCES[this.state.selectedSource].isExternal) {
          this.handleImportItem(chosenItem);
        } else {
          this.props.onChoose(chosenItem.asset);
          this.setState({ isOpen: false });
        }
        return;
      }

      case 'changeSearch':
        return this.setState(
          {
            isConfirmingDelete: false,
            searchTerm: event.value,
          },
          () => this.handleSearchTermChange()
        );

      case 'startDelete':
        return this.setState({ isConfirmingDelete: true });

      case 'cancelDelete':
        return this.setState({ isConfirmingDelete: false });

      case 'confirmDelete': {
        this.setState({ isConfirmingDelete: false });
        return this.deleteSelectedItems();
      }

      case 'close': {
        this.props.onClose();
        return this.setState({ isOpen: false });
      }
    }
  }

  handleSourceChange(newSelectedSource) {
    if (newSelectedSource === 'unsplash') {
      trackUserEvent('unsplash', 'selectedTab');
    }

    const { searchTerm, selectedSource, sources } = this.state;

    this.setState(
      {
        isConfirmingDelete: false,
        searchTerm: '',
        selectedItemIds: [],
        selectedSource: newSelectedSource,
        sources:
          // When switching sources, we clear the search field. If the previous source was an
          // external one (e.g. Unsplash) and the user had entered a search, we need to remove the
          // items that we have stored for that source. Next time the user switches to that source,
          // the default items will load.
          searchTerm && SOURCES[selectedSource].isExternal
            ? { ...sources, [selectedSource]: nullSource }
            : sources,
      },
      () => this.fetchItems(1, false)
    );
  }

  handleSearchTermChange() {
    // This method is debounced in constructor
    if (SOURCES[this.state.selectedSource].fetchByKeyword) {
      this.fetchItems(1, true);
    }
  }

  calculateNewSelectedItemIds(event) {
    const previousSelectedItemIds = this.state.selectedItemIds;

    if (event.shiftKey && previousSelectedItemIds.length) {
      // Replace with range selection
      const items = this.getVisibleItems();

      const clickedIndex = items.findIndex(item => item.id === event.itemId);
      const selectedIndex = Math.min(
        ...previousSelectedItemIds.map(itemId => items.findIndex(item => item.id === itemId))
      );

      const selectionIndices =
        clickedIndex > selectedIndex
          ? _.range(selectedIndex, clickedIndex + 1)
          : _.range(clickedIndex, selectedIndex + 1);

      return selectionIndices.map(index => items[index].id);
    } else if (event.metaKey || event.ctrlKey) {
      // Append to or remove from selection
      return previousSelectedItemIds.includes(event.itemId)
        ? previousSelectedItemIds.filter(itemId => itemId !== event.itemId)
        : [...previousSelectedItemIds, event.itemId];
    } else {
      // Replace selection
      return [event.itemId];
    }
  }

  uploadFiles(files) {
    const { authToken, selectedSource } = this.state;
    const { visibleTypes } = this.props;

    const onlyImages =
      !SOURCES[selectedSource].isExternal && // onlyImages=false allows a higher file size limit
      visibleTypes.length === 1 &&
      visibleTypes[0] === 'images';

    this.setState({
      isConfirmingDelete: false,
      failedUploads: [],
      uploadsInProgress: files.length,
    });

    return new Promise((resolve, reject) => {
      const items = [];

      SOURCES.currentClient.upload(files, authToken, onlyImages).subscribe(
        result => {
          // The Observable will emit a value for each successful or failed upload
          if (result.error) {
            this.setState({
              failedUploads: [
                ...this.state.failedUploads,
                `${result.file.name} (${result.error.message})`,
              ],
              uploadsInProgress: this.state.uploadsInProgress - 1,
            });
          } else {
            items.push(result.item);
            this.setState({
              sources: {
                ...this.state.sources,
                currentClient: {
                  ...this.state.sources.currentClient,
                  items: [...this.state.sources.currentClient.items, result.item],
                },
              },
              selectedItemIds: [result.item.id],
              selectedType: result.item.type,
              uploadsInProgress: this.state.uploadsInProgress - 1,
            });
          }
        },
        error => {
          // onError
          reject(error);
          this.setState({ uploadsInProgress: 0 });
        },
        () => {
          // onComplete
          this.setState({
            searchTerm: '',
            selectedSource: 'currentClient',
            sources: SOURCES[selectedSource].isExternal
              ? { ...this.state.sources, [selectedSource]: nullSource }
              : this.state.sources,
            uploadsInProgress: 0,
          });
          resolve(items);
        }
      );
    });
  }

  handleImportItem(item) {
    const { selectedSource } = this.state;

    this.setState({ uploadsInProgress: 1 });

    // Load image from source into memory
    fetch(item.fullUrl)
      .then(res => res.blob())
      .then(blob => new File([blob], item.filename, { type: 'image/jpeg' }))
      // Upload image to webapp
      .then(file => this.uploadFiles([file]))
      .then(uploadedItems => {
        this.props.onChoose(uploadedItems[0].asset);
        this.setState({ isOpen: false });

        if (SOURCES[selectedSource].onChoose) {
          SOURCES[selectedSource].onChoose(item);
        }
      });
  }

  handleScrollNearBottom() {
    const { selectedSource, sources } = this.state;
    const { isFetching, lastPageFetched, totalPages } = sources[selectedSource];

    if (lastPageFetched < totalPages && !isFetching) {
      this.fetchItems(lastPageFetched + 1, false);
    }
  }

  trackDeleteEvent(assets) {
    assets
      .map(asset => asset.filename)
      .filter(fileName => /^unsplash-/.match(fileName))
      .forEach((fileName) => {
        const [, unsplashId] = fileName.match(/^unsplash-([\w\d]+)(.*)/);

        trackUserEvent('unsplash', 'removedImageFromLibrary', { unsplashId });
      });
  }

  deleteSelectedItems() {
    const { authToken, sources, selectedItemIds, selectedSource } = this.state;

    this.setState({
      selectedItemIds: [],
      sources: {
        ...sources,
        currentClient: {
          ...sources.currentClient,
          items: sources.currentClient.items.filter(item => !selectedItemIds.includes(item.id)),
        },
      },
    });

    const assetsToDelete = selectedItemIds
      .map(itemId => this.state.sources[selectedSource].items.find(item => item.id === itemId))
      .filter(Boolean);

    // The asset delete endpoint accepts asset integer IDs, not UUIDs
    const assetIntegerIds = assetsToDelete
      .map(asset => asset.asset.id);

    SOURCES.currentClient.deleteItems(assetIntegerIds, authToken).catch(() => {});

    this.trackDeleteEvent(assetsToDelete);
  }

  getVisibleItems() {
    const { sources, searchTerm, selectedSource, selectedType, sortAscending, sortBy } = this.state;
    const { items } = sources[selectedSource];

    const sortedItems = SOURCES[selectedSource].isExternal
      ? items
      : [...items] // Clone to prevent Array::sort from mutating state
          .filter(item => item.type === selectedType)
          .sort((a, b) => compareItemsForSort(a, b, sortBy, sortAscending));

    if (SOURCES[selectedSource].filterByKeyword && searchTerm) {
      return SOURCES[selectedSource].filterByKeyword(sortedItems, searchTerm);
    } else {
      return sortedItems;
    }
  }

  render() {
    const {
      failedUploads,
      isConfirmingDelete,
      isOpen,
      searchTerm,
      selectedItemIds,
      selectedSource,
      selectedType,
      sortAscending,
      sortBy,
      sources,
      uploadsInProgress,
    } = this.state;

    const { uploadButtonText, title, visibleTypes } = this.props;

    const sourceConfig = SOURCES[selectedSource];

    return isOpen ? (
      <AssetLibrary
        chooseAllowed={selectedItemIds.length === 1 && !isConfirmingDelete}
        deleteAllowed={Boolean(sourceConfig.deleteItems)}
        dispatch={this.dispatch}
        failedUploads={failedUploads}
        fetchError={sources[selectedSource] ? sources[selectedSource].fetchError : null}
        isConfirmingDelete={isConfirmingDelete}
        isLoading={sources[selectedSource].isFetching && sources[selectedSource].items.length === 0}
        isUploading={uploadsInProgress > 0}
        items={this.getVisibleItems()}
        largeThumbnails={sourceConfig.isExternal === true}
        loadingText={`Loading "${sourceConfig.label}"...`}
        onScrollNearBottom={sourceConfig.isExternal ? this.handleScrollNearBottom : null}
        onSelectFilesForUpload={this.uploadFiles}
        searchTerm={searchTerm}
        selectedItemIds={selectedItemIds}
        selectedSource={selectedSource}
        selectedType={selectedType}
        sortAllowed={!sourceConfig.isExternal}
        sortAscending={sortAscending}
        sortBy={sortBy}
        sourceDescription={sourceConfig.description}
        sourceTotals={generateSourceTotals(sources)}
        title={title}
        typeTotals={generateTypeTotals(sources[selectedSource])}
        uploadAllowed={typeof sourceConfig.upload === 'function'}
        uploadButtonText={uploadButtonText}
        uploadingText={
          sourceConfig.isExternal
            ? 'Importing file into "My Images"...'
            : `Uploading files... ${uploadsInProgress} remaining`
        }
        visibleTypes={visibleTypes}
      />
    ) : null;
  }
}

AssetLibraryContainer.defaultProps = {
  onChoose: () => {},
  onClose: () => {},
  selectedAssetUuids: [],
  selectedType: 'images',
  title: 'Choose Image',
  visibleTypes: Object.keys(TYPES),
};

AssetLibraryContainer.propTypes = {
  isOpen: React.PropTypes.bool.isRequired,
  onChoose: React.PropTypes.func,
  onClose: React.PropTypes.func,
  selectedAssetUuids: React.PropTypes.arrayOf(React.PropTypes.string),
  selectedType: assetTypePropType,
  title: React.PropTypes.string,
  uploadButtonText: React.PropTypes.string,
  visibleTypes: React.PropTypes.arrayOf(assetTypePropType),
};



// WEBPACK FOOTER //
// ./src/components/asset-library/asset-library-container.jsx